import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import ArrowForwardIcon from '@material-ui/icons/ArrowForward'
import clsx from 'clsx'
import Editor from '../../../../../../WYSIWYGEditor'

const useStyles = makeStyles(() => ({
  inputContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    border: 'var(--border-neutral-thin)',
    borderRadius: 'var(--border-radius-lg)',
    background: 'var(--color-surface-050)',
    transition: 'border-color 0.2s ease',
    overflow: 'hidden',
    '&:focus-within': {
      borderColor: 'var(--color-primary-500)'
    },
    '&.__disabled': {
      opacity: 0.6,
      pointerEvents: 'none'
    },
    '& .editor-container': {
      border: 'none'
    },
    '& .editor-inner': {
      background: 'transparent',
      minHeight: '60px',
      maxHeight: '200px',
      overflowY: 'auto'
    },
    '& .editor-input': {
      minHeight: '60px',
      padding: 'var(--dim-2) var(--dim-3)',
      fontSize: 'var(--font-size-sm)',
      color: 'var(--color-neutral-900)'
    },
    '& .editor-placeholder': {
      fontSize: 'var(--font-size-sm)',
      color: 'var(--color-neutral-600)',
      padding: 'var(--dim-2) var(--dim-3)',
      top: 0,
      left: 0
    },
    '& .toolbar': {
      borderBottom: 'var(--border-neutral-thin)',
      padding: 'var(--dim-1) var(--dim-2)',
      background: 'var(--color-neutral-050)'
    }
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: 'var(--dim-1) var(--dim-2)',
    borderTop: 'var(--border-neutral-thin)',
    background: 'var(--color-neutral-050)'
  },
  sendButton: {
    padding: 'var(--dim-2)',
    color: 'var(--color-neutral-500)',
    transition: 'all 0.2s ease',
    background: 'var(--color-neutral-200)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'var(--border-radius-round)',
    border: 'none',
    cursor: 'pointer',
    '&:hover': {
      background: 'var(--color-primary-400)',
      color: 'var(--color-contrast-primary-400)'
    },
    '&.__active': {
      color: 'var(--color-primary-500)'
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed'
    }
  },
  sendIcon: {
    fontSize: '20px'
  }
}))

function CommentInput ({ onSubmit, disabled, placeholder }) {
  const classes = useStyles()
  const [value, setValue] = useState('')
  const [editorKey, setEditorKey] = useState(0)

  const handleSubmit = useCallback(async () => {
    if (!value.trim() || disabled) return

    await onSubmit(value.trim())
    setValue('')
    setEditorKey(prev => prev + 1)
  }, [value, disabled, onSubmit])

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      handleSubmit()
    }
  }, [handleSubmit])

  const hasValue = value.trim().length > 0

  return (
    <div
      className={clsx(classes.inputContainer, { __disabled: disabled })}
      onKeyDown={handleKeyDown}
    >
      <Editor
        key={editorKey}
        placeholder={placeholder}
        isMarkdown
        onChange={setValue}
        defaultValue={value}
      />
      <div className={classes.actions}>
        <button
          className={clsx(classes.sendButton, { __active: hasValue })}
          onClick={handleSubmit}
          disabled={disabled || !hasValue}
          title='Send comment (Cmd/Ctrl + Enter)'
        >
          <ArrowForwardIcon className={classes.sendIcon} />
        </button>
      </div>
    </div>
  )
}

CommentInput.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string
}

CommentInput.defaultProps = {
  disabled: false,
  placeholder: 'Write a comment...'
}

export default CommentInput
